import { useState } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Select,
  InputNumber,
  Space,
  Tag,
  Popconfirm,
  message,
  Spin,
  Empty,
  Divider,
  Descriptions,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  useGetTaskConditionsQuery,
  useCreateTaskConditionMutation,
  useUpdateTaskConditionMutation,
  useDeleteTaskConditionMutation,
  useGetTasksQuery,
  useGetJobVariableDefinitionsQuery,
} from "../../../redux/api/scheduling";

const CONDITION_TYPES = [
  { value: "task", label: "Task Dependency" },
  { value: "datetime", label: "Date/Time Variable" },
];

const GATE_OPTIONS = [
  { value: "start", label: "Allowed to Start" },
  { value: "complete", label: "Allowed to Complete" },
];

const DEPENDENCY_TRIGGER = [
  { value: "started", label: "Task Started" },
  { value: "completed", label: "Task Completed" },
  { value: "percent_complete", label: "Completion Percentage" },
];

const DELAY_METHODS = [
  { value: "real_time", label: "Real Time (365 days)" },
  { value: "weekdays", label: "Weekdays Only" },
  { value: "non_holiday_weekdays", label: "Non-Holiday Weekdays" },
  { value: "business_hours", label: "Business Hours" },
  { value: "staffed_time", label: "Staffed Time" },
];

const LOGIC_OPERATORS = [
  { value: "and", label: "AND" },
  { value: "or", label: "OR" },
];

interface Props {
  taskId: number;
  taskName: string;
}

export default function TaskConditions({ taskId, taskName }: Props) {
  const { data: conditionsData, isLoading } = useGetTaskConditionsQuery(taskId);
  const { data: tasksData } = useGetTasksQuery({});
  const { data: jobVarDefsData } = useGetJobVariableDefinitionsQuery({});
  const [createCondition] = useCreateTaskConditionMutation();
  const [updateCondition] = useUpdateTaskConditionMutation();
  const [deleteCondition] = useDeleteTaskConditionMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCondition, setEditingCondition] = useState<any>(null);
  const [form] = Form.useForm();
  const conditionType = Form.useWatch("condition_type", form);
  const dependencyTrigger = Form.useWatch("dependency_trigger", form);

  const conditions: any[] = conditionsData?.results || conditionsData || [];
  const allTasks: any[] = tasksData?.results || tasksData || [];
  const jobVarDefs: any[] = jobVarDefsData?.results || jobVarDefsData || [];

  const otherTasks = allTasks.filter((t: any) => t.id !== taskId);
  const taskOptions = otherTasks.map((t: any) => ({
    value: t.id,
    label: `[${t.task_id}] ${t.name}`,
  }));

  const dateTimeVarOptions = jobVarDefs
    .filter((v: any) => v.variable_type === "datetime")
    .map((v: any) => ({ value: v.id, label: v.label }));

  const handleOpenCreate = () => {
    setEditingCondition(null);
    form.resetFields();
    form.setFieldsValue({
      condition_type: "task",
      gate: "start",
      dependency_trigger: "completed",
      delay_days: 0,
      delay_hours: 0,
      delay_minutes: 0,
      delay_method: "",
      logic_operator_after: "",
      dependency_task: undefined,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (cond: any) => {
    setEditingCondition(cond);
    form.setFieldsValue({
      condition_type: cond.condition_type,
      gate: cond.gate || "start",
      dependency_trigger: cond.dependency_trigger || "completed",
      dependency_task: cond.dependency_task || undefined,
      datetime_variable: cond.datetime_variable || undefined,
      delay_days: cond.delay_days || 0,
      delay_hours: cond.delay_hours || 0,
      delay_minutes: cond.delay_minutes || 0,
      delay_method: cond.delay_method || "",
      logic_operator_after: cond.logic_operator_after || "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    const payload: any = {
      condition_type: values.condition_type,
      gate: values.gate || "",
      delay_days: values.delay_days || 0,
      delay_hours: values.delay_hours || 0,
      delay_minutes: values.delay_minutes || 0,
      delay_method: values.delay_method || "",
      logic_operator_after: values.logic_operator_after || "",
    };

    if (values.condition_type === "task") {
      payload.dependency_task = values.dependency_task || null;
      payload.dependency_trigger = values.dependency_trigger || "completed";
      payload.datetime_variable = null;
    } else {
      payload.datetime_variable = values.datetime_variable || null;
      payload.dependency_task = null;
      payload.dependency_trigger = "";
    }

    try {
      if (editingCondition) {
        payload.order = editingCondition.order;
        await updateCondition({ id: editingCondition.id, ...payload }).unwrap();
        message.success("Condition updated");
      } else {
        payload.order = conditions.length;
        await createCondition({ taskId, ...payload }).unwrap();
        message.success("Condition created");
      }
      setModalOpen(false);
      form.resetFields();
      setEditingCondition(null);
    } catch (err: any) {
      message.error(err?.data?.detail || JSON.stringify(err?.data) || "Error saving condition");
    }
  };

  const handleDelete = async (condId: number) => {
    try {
      await deleteCondition(condId).unwrap();
      message.success("Condition deleted");
    } catch (err: any) {
      message.error(err?.data?.detail || "Error deleting condition");
    }
  };

  const handleLogicOperatorChange = async (condId: number, value: string) => {
    try {
      await updateCondition({ id: condId, logic_operator_after: value }).unwrap();
    } catch {
      message.error("Failed to update logic operator");
    }
  };

  const getTaskLabel = (depTaskId: number) => {
    const t = allTasks.find((t: any) => t.id === depTaskId);
    return t ? `[${t.task_id}] ${t.name}` : `Task #${depTaskId}`;
  };

  if (isLoading) return <Spin size="large" className="mt-10 mx-auto block" />;

  return (
    <div>
      <Card
        title={`Conditions for: ${taskName}`}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Add Condition
          </Button>
        }
      >
        <p className="text-sm text-gray-500 mb-4">
          Conditions define what must be satisfied before a task can start or complete.
          Multiple conditions can be chained with AND/OR logic between them.
        </p>

        {conditions.length === 0 ? (
          <Empty
            description="No conditions defined for this task"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleOpenCreate}>
              Add first condition
            </Button>
          </Empty>
        ) : (
          <div className="space-y-3">
            {conditions.map((cond: any, idx: number) => (
              <div key={cond.id}>
                <Card
                  size="small"
                  className="border-l-4"
                  style={{
                    borderLeftColor:
                      cond.condition_type === "task" ? "#1890ff" : "#faad14",
                  }}
                  extra={
                    <Space>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenEdit(cond)}
                      />
                      <Popconfirm
                        title="Delete this condition?"
                        onConfirm={() => handleDelete(cond.id)}
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Space>
                  }
                >
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Type">
                      <Tag color={cond.condition_type === "task" ? "blue" : "gold"}>
                        {cond.condition_type === "task"
                          ? "Task Dependency"
                          : "Date/Time"}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Gate">
                      <Tag color={cond.gate === "start" ? "green" : "purple"}>
                        {cond.gate === "start" ? "Start" : "Complete"}
                      </Tag>
                    </Descriptions.Item>

                    {cond.condition_type === "task" && (
                      <>
                        <Descriptions.Item label="Dependency Task" span={2}>
                          {cond.dependency_task ? (
                            <Tag>
                              [{cond.dependency_task_id_display}]{" "}
                              {cond.dependency_task_name}
                            </Tag>
                          ) : (
                            <span className="text-gray-400 italic">None</span>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Trigger">
                          <Tag color={cond.dependency_trigger === "percent_complete" ? "volcano" : undefined}>
                            {DEPENDENCY_TRIGGER.find((t) => t.value === cond.dependency_trigger)?.label ||
                              cond.dependency_trigger}
                          </Tag>
                        </Descriptions.Item>
                      </>
                    )}

                    {cond.condition_type === "datetime" && (
                      <Descriptions.Item label="Variable">
                        <Tag color="gold">
                          {cond.datetime_variable_label || `Var #${cond.datetime_variable}`}
                        </Tag>
                      </Descriptions.Item>
                    )}

                    {(cond.delay_days > 0 ||
                      cond.delay_hours > 0 ||
                      cond.delay_minutes > 0) && (
                      <>
                        <Descriptions.Item label="Delay">
                          {cond.delay_days > 0 && `${cond.delay_days}d `}
                          {cond.delay_hours > 0 && `${cond.delay_hours}h `}
                          {cond.delay_minutes > 0 && `${cond.delay_minutes}m`}
                        </Descriptions.Item>
                        <Descriptions.Item label="Delay Method">
                          <Tag>
                            {DELAY_METHODS.find(
                              (m) => m.value === cond.delay_method
                            )?.label || cond.delay_method}
                          </Tag>
                        </Descriptions.Item>
                      </>
                    )}
                  </Descriptions>
                </Card>

                {idx < conditions.length - 1 && (
                  <div className="flex items-center justify-center my-2">
                    <Select
                      size="small"
                      value={cond.logic_operator_after || "and"}
                      options={LOGIC_OPERATORS}
                      onChange={(v) => handleLogicOperatorChange(cond.id, v)}
                      style={{ width: 100 }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        title={editingCondition ? "Edit Condition" : "Add Condition"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setEditingCondition(null);
        }}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="condition_type"
            label="Condition Type"
            rules={[{ required: true }]}
          >
            <Select options={CONDITION_TYPES} />
          </Form.Item>

          <Form.Item
            name="gate"
            label="Gate"
            rules={[{ required: true }]}
            tooltip="Determines if this condition controls when the task can START or COMPLETE"
          >
            <Select options={GATE_OPTIONS} />
          </Form.Item>

          <Divider plain>
            {conditionType === "datetime"
              ? "Date/Time Settings"
              : "Task Dependency Settings"}
          </Divider>

          {conditionType === "task" && (
            <>
              <Form.Item
                name="dependency_task"
                label="Dependency Task"
                rules={[
                  {
                    required: true,
                    message: "Select a dependency task",
                  },
                ]}
                tooltip="The task this condition depends on"
              >
                <Select
                  options={taskOptions}
                  placeholder="Select dependency task..."
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label?.toString() || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="dependency_trigger"
                label="Dependency Trigger"
                rules={[{ required: true }]}
                tooltip="Determines if the dependency task needs to be STARTED, COMPLETED, or reach a completion percentage to satisfy this condition"
              >
                <Select options={DEPENDENCY_TRIGGER} />
              </Form.Item>
              {dependencyTrigger === "percent_complete" && (
                <p className="text-xs text-gray-500 -mt-4 mb-4">
                  Used with batch tasks. The trigger percentages are auto-calculated from the batch method
                  and job quantity. The dependency task should be a non-batch task (e.g. Machining).
                </p>
              )}
            </>
          )}

          {conditionType === "datetime" && (
            <Form.Item
              name="datetime_variable"
              label="Date/Time Variable"
              rules={[{ required: true, message: "Select a date/time variable" }]}
              tooltip="The job date/time variable used for this condition"
            >
              <Select
                options={dateTimeVarOptions}
                placeholder="Select a date/time variable..."
                allowClear
              />
            </Form.Item>
          )}

          <Divider plain>Delay (optional)</Divider>

          <div className="grid grid-cols-3 gap-2">
            <Form.Item name="delay_days" label="Days">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
            <Form.Item name="delay_hours" label="Hours">
              <InputNumber min={0} max={23} className="w-full" />
            </Form.Item>
            <Form.Item name="delay_minutes" label="Minutes">
              <InputNumber min={0} max={59} className="w-full" />
            </Form.Item>
          </div>

          <Form.Item
            name="delay_method"
            label="Delay Method"
            tooltip="How the delay duration is counted"
          >
            <Select
              options={DELAY_METHODS}
              placeholder="Select delay method (required if delay > 0)"
              allowClear
            />
          </Form.Item>

          <Divider plain>Inter-Condition Logic</Divider>

          <Form.Item
            name="logic_operator_after"
            label="Logic Operator After This Condition"
            tooltip="AND/OR connecting this condition to the next one. Leave empty for the last condition."
          >
            <Select
              options={[
                { value: "", label: "(None — last condition)" },
                ...LOGIC_OPERATORS,
              ]}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
